import StatisticFunctionConfigDialog from '../../dialog/StatisticFunctionConfigDialog.js';

export default class StatisticComponents {
	/**
	 * @param {Drawer} drawer
	 * @param {ChartDataBuilder} dataBuilder
	 */
	constructor(drawer, dataBuilder) {
		this._drawer = drawer;
		this._dataBuilder = dataBuilder;
		/** @type {StatisticFunctionComponent[]} */
		this._components = [];
		this._callbacks = {};
	}

	on(eventName, cb) {
		this._callbacks[eventName] = cb;
	}

	async updateAll() {
		for (const component of this._components) {
			await this.updateComponent(component)
		}
	}

	async updateComponent(component) {
		const {x, y} = this._dataBuilder.lastResult.getVisiblePoints();
		const functionConfig = component.getFunctionConfig();
		let functionResult = null;
		if (StatisticComponents.isLineCalculable(functionConfig, x, y)) {
			let xDomain = [-Infinity, Infinity], yDomain = [-Infinity, Infinity];
			if (component instanceof RegressionFunctionComponent) {
				xDomain = this._drawer._scale.x.domain;
				yDomain = this._drawer._scale.y.domain;
			}
			functionResult = await functionConfig.createLine(x, y, xDomain, yDomain);
		}
		component.setFunctionResult(functionResult);
	}

	/**
	 * @param {LineConfig} lineConfig
	 * @param {RegressionApiClient} regressionApiClient
	 * @return {StatisticFunctionComponent}
	 */
	async create(lineConfig, regressionApiClient) {
		let cmp;
		switch (lineConfig.functionConfig.type) {
			case "mean": {
				cmp = new MeanFunctionComponent(lineConfig, this._drawer);
				break;
			}
			case "sd": {
				cmp = new SdFunctionComponent(lineConfig, this._drawer);
				break;
			}
			case "percentile": {
				cmp = new PercentileFunctionComponent(lineConfig, this._drawer);
				break;
			}
			case "regression": {
				lineConfig.functionConfig.params.client = regressionApiClient;
				cmp = new RegressionFunctionComponent(lineConfig, this._drawer);
				break;
			}
			default :
				throw new Error(`Such type[ ${lineConfig.functionConfig.type} ] is not found`);
		}
		this._components.push(cmp)
		cmp.on('change', async () => {
			await this.updateComponent(cmp)
			if (typeof this._callbacks['change'] === "function")
				await this._callbacks['change'](cmp)
		})
		cmp.on('remove', async () => {
			this._components = this._components.filter(comp => comp !== cmp)
			if (typeof this._callbacks['change'] === "function")
				await this._callbacks['change'](cmp)
		})
		await this.updateComponent(cmp);
		return cmp;
	}

	toServerJson() {
		return this._components.map(cmp => cmp.toServerJson())
	}

	/**
	 * @param {LineConfig} lineConfig
	 * @param {number[]} x
	 * @param {number[]} y
	 * @return {boolean}
	 * @private
	 */
	static isLineCalculable(lineConfig, x, y) {
		if (!y.every(val => Number.isFinite(val)))
			return false;
		switch (lineConfig.functionConfig.type) {
			case "mean":
			case "sd":
			case "percentile":
				return y.length > 1
			case "regression":
				return x.length > 1 && x.findIndex((val, i) => i !== 0 && val !== x[0]) !== -1
			default :
				throw new Error(`Such type[ ${lineConfig.functionConfig.type} ] is not found`);
		}
	}
}

class StatisticFunctionComponent {
	/**
	 * @param {LineConfig} lineConfig
	 * @param {Drawer} drawer
	 */
	constructor(lineConfig, drawer) {
		this._lineConfig = lineConfig;
		this._drawer = drawer;
		this._callbacks = [];
		this._elements = [];
	}

	on(eventName, cb) {
		this._callbacks[eventName] = cb;
	};

	setFunctionResult(result) {
		let lineResult = {points: null, label: ''}
		if (Array.isArray(result))
			lineResult = result[0];
		this._line.points = lineResult.points;
		this._line.text = lineResult.label;
	}

	getFunctionConfig() {
		return this._lineConfig;
	}

	showConfigDialog() {
		let dialog = new StatisticFunctionConfigDialog().render(this._getDialogTemplate());
		dialog.on('ok', async () => {
			const paramsChanged = this.onDialogOk(dialog);
			this.updateStyle();
			if (paramsChanged && typeof this._callbacks['change'] === "function")
				await this._callbacks['change']();
			dialog.remove();
		})
		dialog.on('delete', () => {
			this.remove()
			if (typeof this._callbacks['remove'] === "function")
				this._callbacks['remove']();
		})
	}

	/**
	 * @param dialog
	 * @return boolean
	 */
	onDialogOk(dialog) {
		return this.updateProperties(dialog);
	}

	updateProperties(dialog){
		const labelPattern = dialog._dialog.querySelector('#label-pattern').value.trim();
		const lineType = dialog._dialog.querySelector('option:checked').value;
		const lineColor = dialog._dialog.querySelector('.color-preview-rect').style.backgroundColor;
		const paramsChanged = this._lineConfig.functionConfig.labelPattern !== labelPattern
			|| this._lineConfig.lineStyle.type !== lineType || this._lineConfig.lineStyle.color !== lineColor;
		this._lineConfig.functionConfig.labelPattern = labelPattern
		this._lineConfig.lineStyle.type = lineType;
		this._lineConfig.lineStyle.color = lineColor;
		return paramsChanged;
	}

	updateStyle() {
		throw new Error('Not implemented')
	}

	remove() {
		for (const element of this._elements)
			this._drawer.removeElement(element)
	}

	toServerJson() {
		return {
			lineFunction: {
				type: this._lineConfig.functionConfig.type,
				params: this._lineConfig.functionConfig.params,
				labelPattern: this._lineConfig.functionConfig.labelPattern
			},
			lineStyle: this._lineConfig.lineStyle
		}
	}

	_getDialogTemplate() {
		throw new Error('Not implemented')
	}

	_getLineOptionsList(selectedOption) {
		return `<label for="types">Line type: </label>
		<select id="types">
			<option value="solid" ${selectedOption === 'solid' ? 'selected' : ''}>solid</option>
			<option value="dotted" ${selectedOption === 'dotted' ? 'selected' : ''}>dotted</option>
			<option value="dashed" ${selectedOption === 'dashed' ? 'selected' : ''}>dashed</option>
		</select>`
	}

	_setElements(elements) {
		this._elements = elements;
		for (const element of elements)
			element.group.addEventListener('click', () => {
				this.showConfigDialog()
			});
	}
}

class MeanFunctionComponent extends StatisticFunctionComponent {
	/**
	 * @param {LineConfig} lineConfig
	 * @param {Drawer} drawer
	 */
	constructor(lineConfig, drawer) {
		super(lineConfig, drawer);
		this._line = drawer.drawLabeledLine([], '', {"data-line-type": lineConfig.lineStyle.type});
		this._line.setColor(lineConfig.lineStyle.color)
		this._setElements([this._line])
	}

	updateStyle() {
		this._line.group.setAttribute('data-line-type', this._lineConfig.lineStyle.type);
		this._line.setColor(this._lineConfig.lineStyle.color);
	}

	_getDialogTemplate() {
		return `<p class="statistic-function-config-dialog__title">Mean</p>
				<text-input 
					id="label-pattern" 
					value="${this._lineConfig.functionConfig.labelPattern.replace(/"/g, "&quot;")}" 
					label="Label pattern"
					maxLength="500"
					required>
				</text-input>
				<div id="function-style-color">
                	<span>Line color: </span><div class="color-preview-rect" style="background-color: ${this._lineConfig.lineStyle.color}"></div>
                	<color-palette hidden></color-palette>
              	</div>
				<div id="line-type">		
					${this._getLineOptionsList(this._lineConfig.lineStyle.type)}	
				</div>
		`;
	}
}

class SdFunctionComponent extends StatisticFunctionComponent {
	/**
	 * @param {LineConfig} lineConfig
	 * @param {Drawer} drawer
	 */
	constructor(lineConfig, drawer) {
		super(lineConfig, drawer);
		this._drawer = drawer;
		this._line1 = drawer.drawLabeledLine([], '', {"data-line-type": lineConfig.lineStyle.type});
		this._line1.setColor(lineConfig.lineStyle.color)
		this._line2 = drawer.drawLabeledLine([], '', {"data-line-type": lineConfig.lineStyle.type});
		this._line2.setColor(lineConfig.lineStyle.color)
		this._setElements([this._line1, this._line2])
	}

	setFunctionResult(result) {
		let lineResult = [{points: null, label: ''}, {points: null, label: ''}]
		if (Array.isArray(result))
			lineResult = result;
		this._line1.points = lineResult[0].points;
		this._line1.text = lineResult[0].label;
		this._line2.points = lineResult[1].points;
		this._line2.text = lineResult[1].label;

		this._line1.group.style.display = 'none'
		this._line2.group.style.display = 'none'

		if (this._isLineResultVisible(lineResult[0]))
			this._line1.group.style.display = null
		if (this._isLineResultVisible(lineResult[1]))
			this._line2.group.style.display = null
	}

	onDialogOk(dialog) {
		let sd = parseFloat(dialog._dialog.querySelector('#sd-number').value);
		// In the DB some of the previous saved values have string type. So we will replace them with numbers
		let sdChanged = this._lineConfig.functionConfig.params.numOfSd !== sd;
		this._lineConfig.functionConfig.params.numOfSd = sd;
		return this.updateProperties(dialog) || sdChanged;
	}

	updateStyle() {
		this._line1.group.setAttribute('data-line-type', this._lineConfig.lineStyle.type);
		this._line2.group.setAttribute('data-line-type', this._lineConfig.lineStyle.type);
		this._line1.setColor(this._lineConfig.lineStyle.color);
		this._line2.setColor(this._lineConfig.lineStyle.color);
	}

	_isLineResultVisible(lineResult) {
		if (lineResult.points === null)
			return false;
		const scale = this._drawer._scale.y;
		let y0 = scale.toPx(lineResult.points[0][1]), y1 = scale.toPx(lineResult.points[1][1]);
		return y0 >= scale.range[1] && y1 <= scale.range[0];
	}

	_getDialogTemplate() {
		return `<p class="statistic-function-config-dialog__title">Standard deviation</p>
				<text-input 
					id="label-pattern" 
					value="${this._lineConfig.functionConfig.labelPattern.replace(/"/g, "&quot;")}" 
					label="Label pattern" 
					maxLength="500"
					required></text-input>
				<text-input 
					id="sd-number" 
					value="${this._lineConfig.functionConfig.params.numOfSd}" 
					label="Number of SD's" 
					type="number" 
					min="0"
					required></text-input>
				<div id="function-style-color">
                	<span>Line color: </span><div class="color-preview-rect" style="background-color: ${this._lineConfig.lineStyle.color}"></div>
                	<color-palette hidden></color-palette>
              	</div>
				<div id="line-type">		
					${this._getLineOptionsList(this._lineConfig.lineStyle.type)}	
				</div>
		`;
	}
}

class PercentileFunctionComponent extends StatisticFunctionComponent {
	/**
	 * @param {LineConfig} lineConfig
	 * @param {Drawer} drawer
	 */
	constructor(lineConfig, drawer) {
		super(lineConfig, drawer);
		this._line = drawer.drawLabeledLine([], '', {"data-line-type": lineConfig.lineStyle.type});
		this._line.setColor(lineConfig.lineStyle.color)
		this._setElements([this._line])
	}

	onDialogOk(dialog) {
		let percentile = parseFloat(dialog._dialog.querySelector('#percentile').value);
		// In the DB some of the previous saved values have string type. So we will replace them with numbers
		const percentileChanged = this._lineConfig.functionConfig.params.percentile !== percentile;
		this._lineConfig.functionConfig.params.percentile = percentile;
		return super.onDialogOk(dialog) || percentileChanged;
	}

	updateStyle() {
		this._line.group.setAttribute('data-line-type', this._lineConfig.lineStyle.type);
		this._line.setColor(this._lineConfig.lineStyle.color);
	}

	_getDialogTemplate() {
		return `<p class="statistic-function-config-dialog__title">Percentile</p>
				<text-input 
					id="label-pattern" 
					value="${this._lineConfig.functionConfig.labelPattern.replace(/"/g, "&quot;")}" 
					label="Label pattern" 
					maxLength="500"
					required></text-input>
				<text-input 
					id="percentile" 
					value="${this._lineConfig.functionConfig.params.percentile}" 
					label="Percentile" 
					type="number" 
					min="0"
					max="1" 
					required></text-input>
				<div id="function-style-color">
                	<span>Line color: </span><div class="color-preview-rect" style="background-color: ${this._lineConfig.lineStyle.color}"></div>
                	<color-palette hidden></color-palette>
              	</div>
				<div id="line-type">		
					${this._getLineOptionsList(this._lineConfig.lineStyle.type)}	
				</div>
		`;
	}
}

class RegressionFunctionComponent extends StatisticFunctionComponent {
	/**
	 * @param {LineConfig} lineConfig
	 * @param {Drawer} drawer
	 */
	constructor(lineConfig, drawer) {
		super(lineConfig, drawer);
		this._drawer = drawer;
		this._line = drawer.drawLabeledLine([], '', {"data-line-type": lineConfig.lineStyle.type});
		this._line.setColor(lineConfig.lineStyle.color)
		this._setElements([this._line])
	}

	updateStyle() {
		this._line.group.setAttribute('data-line-type', this._lineConfig.lineStyle.type);
		this._line.setColor(this._lineConfig.lineStyle.color);
	}

	_getDialogTemplate() {
		return `<p class="statistic-function-config-dialog__title">Regression</p>
				<text-input 
					id="label-pattern" 
					value="${this._lineConfig.functionConfig.labelPattern.replace(/"/g, "&quot;")}" 
					label="Label pattern (e.g. \${polynomial}, \${r2})">
				</text-input>
				<div id="function-style-color">
                	<span>Line color: </span><div class="color-preview-rect" style="background-color: ${this._lineConfig.lineStyle.color}"></div>
                	<color-palette hidden></color-palette>
              	</div>
				<div id="line-type">		
					${this._getLineOptionsList(this._lineConfig.lineStyle.type)}	
				</div>
		`;
	}
}